/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { downloadZipPdfs } from '../../Service/Api';
import { ToastList } from '../Dialog/Dialog';

import './DownloadZipModal.scss';

const generateFilename = (loteIndex = null) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const suffix = loteIndex !== null ? `_lote${loteIndex}` : '';
    return `${timestamp}_${Math.floor(Math.random() * 1001)}${suffix}.zip`;
};

export const triggerDownload = async (ids, loteIndex = null) => {
    const response = await downloadZipPdfs(ids);
    const filename = generateFilename(loteIndex);
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
};

const DownloadZipModal = ({ isOpen, toggle, ids }) => {
    const [step, setStep] = useState(1);
    const [chunkSize, setChunkSize] = useState('');
    const [chunks, setChunks] = useState([]);
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((success, message) => {
        setToasts((prev) => [...prev, { id: Date.now(), success, message }]);
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const handleToggle = () => {
        setStep(1);
        setChunkSize('');
        setChunks([]);
        toggle();
    };

    const handleGenerateChunks = () => {
        const size = parseInt(chunkSize, 10);
        if (!size || size < 1) return;
        const result = [];
        for (let i = 0; i < ids.length; i += size) {
            result.push(ids.slice(i, i + size));
        }
        setChunks(result);
        setStep(2);
    };

    const handleDownloadChunk = async (chunk, index) => {
        try {
            await triggerDownload(chunk, index);
            addToast(true, `Lote ${index} descargado correctamente`);
        } catch {
            addToast(false, `Error al descargar el lote ${index}`);
        }
    };

    return (
        <>
            <ToastList toasts={toasts} onDismiss={dismissToast} />

            <Modal isOpen={isOpen} toggle={handleToggle} className="zip-modal">
                <ModalHeader toggle={handleToggle}>
                    Descarga por lotes
                </ModalHeader>

                <ModalBody className="zip-modal-body">
                    {step === 1 && (
                        <>
                            <p className="zip-chunk-info">
                                Total de archivos:
                                {' '}
                                <strong>{ids.length}</strong>
                            </p>
                            <label className="zip-chunk-label" htmlFor="chunk-size">
                                Archivos por lote
                                <div className="input-container">
                                    <input
                                        id="chunk-size"
                                        type="number"
                                        min="1"
                                        max={ids.length}
                                        value={chunkSize}
                                        onChange={(e) => setChunkSize(e.target.value)}
                                        placeholder="Ej: 10"
                                    />
                                </div>
                            </label>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <p className="zip-chunk-info">
                                {chunks.length}
                                {' '}
                                {chunks.length !== 1 ? 'lotes generados' : 'lote generado'}
                            </p>
                            <ul className="zip-chunk-list">
                                {chunks.map((chunk, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <li key={index} className="zip-chunk-item">
                                        <button
                                            className="zip-chunk-link"
                                            type="button"
                                            onClick={() => handleDownloadChunk(chunk, index + 1)}
                                        >
                                            <i className="fas fa-download" />
                                            {` Lote ${index + 1} — ${chunk.length} archivo${chunk.length !== 1 ? 's' : ''}`}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </ModalBody>

                <ModalFooter className="zip-modal-footer">
                    {step === 1 ? (
                        <button
                            className="button-main"
                            type="button"
                            disabled={!chunkSize || Number(chunkSize) < 1}
                            onClick={handleGenerateChunks}
                        >
                            Generar enlaces
                        </button>
                    ) : (
                        <button
                            className="button-main zip-chunk-back"
                            type="button"
                            onClick={() => setStep(1)}
                        >
                            Volver
                        </button>
                    )}
                </ModalFooter>
            </Modal>
        </>
    );
};

export default DownloadZipModal;
